<template>
  <config-base class="opc-webinar-edit" ref="baseRef" :on="$listeners">
    <template #config>
      <bought-license :type="planTypes.WEBINAR" />
      <capacity-config-list ref="configListRef" :config-plan-type="planType" :config-capacity-key="capacityKey" capacityUnitI18nKey='billing.attendees' :configRowName='configRowName'></capacity-config-list>
      <div
        style="margin-top: 24px;"
        v-if="addMoreVisible"
        v-html="$t('billing.webinar.add_more_plan_link_capacity_new', [title, addFlowLink])"
        v-intercept-link="handleAddMore"
      >
      </div>
      <preview-payment class="opc-webinar-edit__preview-payment" />
    </template>
  </config-base>
</template>

<script>
import _, {get, map} from "lodash";
import {mapGetters} from "vuex";
import {
  BILLING_CYCLE, planTypes, SUBSCRIPTION_TYPE,
  WEBINAR_ONE_TIME_CAPACITY,
} from '@/common/constants';
import planCommon from '@/plans/WEBINAR/mixin';
import {
  computedCapacityWebinarPlans,
  computedWebinarPlans,
} from "@/common/service/computers/plans/webinar";
import {getPlanTotalLicense} from "@/common/service/validate";
import { pageConfig } from '@/common/service/page-config';
import {getNeedFilterCapacityList} from '@/common/product';
import plansMixin from "@/plans/mx/plan";
import ConfigList from "@/components/capacity/config-list.vue";
import ConfigBase from "@/plans/WEBINAR/config/base.vue";
import addonMixin from "@/plans/mx/addon";
import replenishMixin from "@/components/capacity/mx/replenish";
import standardPriceMixin from '@/components/capacity/mx/standard-price';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import capacityPlanMixin from '@/components/capacity/mx/capacity-plan';
import { plansof } from '@/utils/chain';
import { enableNewUIWebinarOneTimePlan, webinarSingleUseAllowed } from '@/common/service/op-switch';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import { jumpToTargetPageForDiffType } from '@/utils/modal-utils';

const CapacityConfigList = {
  name: 'CapacityConfigList',
  extends: ConfigList,
  mixins: [planCommon, addonMixin, replenishMixin, standardPriceMixin, capacityPlanMixin],
  data () {
    return {
      title: this.$t('billing.webinar.title'),
      msgInstance: null,
    }
  },
  computed: {
    ...mapGetters(['getCurrentMeetingPlans', 'getCurrentBasePlanInfo', 'boughtPlans', 'getBoughtMeetingPlans', 'basicConfig','currentEditPlan', 'assignInfo']),
    boughtCurrentPlans() {
      return computedWebinarPlans(this.boughtPlans || [])
    },
    computedSupportCapacity() {
      const boughtCapacity = map(this.boughtCurrentPlans, this.capacityKey);
      return [...new Set([...this.supportCapacity, ...boughtCapacity])].sort((a,b) => a - b);
    },
    planList() {
      return _.chain(this.planMapFlat)
        .filter((p) => {
          const isCurrentPlanType = p.type === this.planType;
          const isSupportCapacity = this.computedSupportCapacity.includes(p[this.capacityKey]);
          const needFilterCapacity = getNeedFilterCapacityList(this.getCurrentBasePlanInfo?.plans).includes(p[this.capacityKey])
          const isBoughtCapacity = this.boughtCurrentPlans.map(k=>
            k.isFreeTrial? k.capacity: k[this.capacityKey]
          ).includes(p[this.capacityKey])
          const otherBoughtCapacity = isBoughtCapacity  && this.currentEditPlan?.participants !== p[this.capacityKey]
          const selectablePlanList = isCurrentPlanType && isSupportCapacity && !needFilterCapacity && !otherBoughtCapacity;
          return this.isBulkLicenseAccount? (selectablePlanList && isBoughtCapacity) : selectablePlanList
        })
        .filter((p) => {
          const currentCapacity = _.get(this.currentEditPlan, [this.capacityKey]);

          // with cancellation coupon campaign case and current edit plan is pending status, do not allow switch to other capacity
          if (this.fromCancelCampaignWithPendingCase) {
            return p.capacity === currentCapacity;
          }

          // Can only be upgraded to a higher capacity
          if (this.disableDownSell && currentCapacity) {
            return p.capacity >= currentCapacity;
          }

          // OBT-5767
          if (this.shouldSoftBlockOneMonth) {
            return p.capacity >= currentCapacity;
          }

          return p;
        })
        .value();
    },
    // OBT-5767: soft block modal from 'webinar schedule': 1. block down-sell; 2. cycle only include annual and monthly
    shouldSoftBlockOneMonth() {
      return !!pageConfig?.softWbnSub && this.currentEditPlan?.billing_cycle === BILLING_CYCLE.ONE_MONTH;
    },
    currentBasePlans() {
      return this.getCurrentMeetingPlans.length ? this.getCurrentMeetingPlans : this.getCurrentBasePlanInfo?.plans;
    },
    boughtCapacityList() {
      return [...new Set(map(this.boughtCurrentPlans, this.capacityKey))].filter(capacity => this.computedSupportCapacity.includes(capacity));
    },
    isBulkLicenseAccount() {
      return this.basicConfig?.isBulkLicenseAccount
    },
    disableDownSell() {
      return this.basicConfig?.downSellData?.disableDownSell
    },
    mx_planLicenseCount() {
      return this.mx_totalCapacityLicenseCount(this.visibleCapacityList[0], this.capacityKey);
    },
    editTotalLimitDesc() {
      const basePlanNameKey = get(this.currentBasePlans, [0, 'parentPlanNameKey']) || get(this.currentBasePlans, [0, 'planNameKey']) || get(this.currentBasePlans, [0, 'typeNameKey'])
      return this.$t('billing.addon_license_limit_tips', [this.title, this.$t(basePlanNameKey)])
    },
    addCapacityBtnVisible() {
      return false;
    },
    mx_showSelectCapacityDropdown() {
      return this.allCapacityOrderList.length > 1;
    },
  },
  methods: {
    afterSwitchCapacityHook(oldSelectedPlan, newPlan) {
      if (!this.mx_showSelectCapacityDropdown) return
      this.mx_checkCapacityChanged(newPlan)
    },
    mx_getBillingCycleList(capacity) {
      const { capacityMap } = this;

      const capacityPlanList = capacityMap[capacity] || [];
      const monthlyWebinarPlans = plansof(this.boughtCurrentPlans).monthly().get();
      const annualWebinarPlans = plansof(this.boughtCurrentPlans).annual().get();
      const oneMonthWebinarPlans = plansof(this.boughtCurrentPlans).one_month().category(pageConfig?.subPlan).get();
      const hideOneMonth = (monthlyWebinarPlans.length || annualWebinarPlans.length) && oneMonthWebinarPlans.length === 0;

      const currentMonthlyPlans4Capacity = plansof(monthlyWebinarPlans).capacity(capacity).get();
      const currentAnnualPlans4Capacity = plansof(annualWebinarPlans).capacity(capacity).get();

      const monthlyOptions = this.getBillingCycleOptions(currentMonthlyPlans4Capacity, undefined, oneMonthWebinarPlans);
      const annualOptions = this.getBillingCycleOptions(currentAnnualPlans4Capacity, {
        disabled: this.mx_annualPlanDisabled.disabled,
        reason: this.mx_annualPlanDisabled.reason,
      }, oneMonthWebinarPlans);

      return [
        {
          plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.ONE_MONTH),
          // OBT-5767: soft block modal: cycle only include annual and monthly
          hidden: hideOneMonth || this.shouldSoftBlockOneMonth,
        },
        {
          plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.MONTHLY),
          disabled: monthlyOptions.disabled,
          tooltips: monthlyOptions.reason,
        },
        {
          plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.ANNUAL),
          disabled: annualOptions.disabled,
          tooltips: annualOptions.reason,
        },
        {
          billingCycle: BILLING_CYCLE.QUARTERLY,
          plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.QUARTERLY),
        },
        {
          billingCycle: BILLING_CYCLE.TWO_YEARS,
          plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.TWO_YEARS),
        },
        {
          billingCycle: BILLING_CYCLE.THREE_YEARS,
          plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.THREE_YEARS),
        },
      ].filter(p => p.plan && !p.hidden);
    },
    initVisibleCapacityList() {
      if (this.selectedPlanList?.length) {
        // ZOOM-553979
        this.visibleCapacityList = map(this.selectedPlanList.filter(p => p.category === pageConfig?.subPlan || this.mx_showSelectCapacityDropdown), this.capacityKey);
      } else {
        this.visibleCapacityList = this.allCapacityOrderList.slice(0, 1);
      }

      // OBT-5767: soft block modal: init cycle excluding one-month
      if (this.shouldSoftBlockOneMonth) {
          const curCapacity = this.currentEditPlan?.capacity;
          const nextPlan = this.mx_getBillingCycleList(curCapacity).filter(p => !p.disabled && p.plan?.billing_cycle !== BILLING_CYCLE.ONE_MONTH)?.[0];
          this.$nextTick(async () => {
            await this.$refs[`configRow${curCapacity}`]?.[0]?.switchBillingCycle({ ...this.currentEditPlan }, nextPlan?.plan);
            this.$refs[`configRow${curCapacity}`]?.[0]?.changeLicense(this.currentEditPlan?.cartInfo?.selected_number);
          })
      }
    },
    handleLicenseChange(license) {
      this.mx_validDownSellLicense(license)
      if (license >= this.mx_getMaxHosts() && !this.mx_canAutoAdd && this.mx_getMaxHosts() !== 999) {
        this.mx_licenseAlert = this.editTotalLimitDesc
      } else if (this.mx_downSellLicenseErrorMessage) {
        this.mx_licenseAlert = this.mx_downSellLicenseErrorMessage
      } else {
        this.mx_licenseAlert = ''
      }
    },
    getCurrentCapacityList() {
      return this.allCapacityOrderList
    },
    showMessage(message) {
      this.msgInstance && this.msgInstance.close();
      this.msgInstance = this.$message({
        type: 'info',
        duration: 8000,
        message: message,
        showClose: true
      })
    },
    mx_getMinHosts(capacity) {
      // non standard price
      if (this.checkCurrentCapacityIsNonStandardPrice(capacity)) {
        return this.currentBoughtLicense;
      }

      // for edit
      if (this.disableDownSell) {
        return getPlanTotalLicense([this.currentEditPlan]) || 1;
      }

      // for pending plan with cancellation coupon
      if (this.fromCancelCampaignWithPendingCase) {
        return this.currentBoughtLicense;
      }

      if (this.shouldSoftBlockOneMonth) {
        const assignInfo = this.assignInfo[this.$config('oldPlanCode')];
        return assignInfo?.assignLicenseQuantity || 1;
      }

      return 1
    },
    mx_getMaxHosts() {
      const basePlanTotalLicenses = getPlanTotalLicense(this.currentBasePlans || []);
      if (this.mx_canAutoAdd || basePlanTotalLicenses <= 0) {
        return 999;
      }
      // when number of current plan license exceed the number of base plan license,
      // use the maximum one
      const currentEditPlanLicenses = get(this.currentEditPlan, 'cartInfo.selected_number', 0);
      return Math.max(basePlanTotalLicenses, currentEditPlanLicenses);
    },
    mx_getDecreaseTips(capacity) {
      const currentCapacityPlanCount = getPlanTotalLicense(computedCapacityWebinarPlans(this.selectedPlanList || [], capacity));

      const currentIsEqualPrev = currentCapacityPlanCount > 1 && currentCapacityPlanCount === this.mx_getMinHosts(capacity);

      if (this.checkCurrentCapacityIsNonStandardPrice(capacity) && currentIsEqualPrev) {
        return this.$t('billing.planform.meeting_license_custom_pricing');
      }

      // for pending plan with cancellation coupon
      if (this.fromCancelCampaignWithPendingCase && currentIsEqualPrev) {
        return this.$t('billing.coupon.not_switch_plan_pending_plans');
      }

      if (currentIsEqualPrev) {
        return this.$t('billing.error.can_not_bought_plan_tips')
      }
      return ''
    },
    mx_onAddedBasePlan(basePlanName) {
      this.showMessage(
        this.$t('billing.addon_auto_add_tips', [this.title, basePlanName]),
      );
    },
    mx_onRemovedBasePlan(basePlanName) {
      this.showMessage(
        this.$t('billing.addon_auto_remove_tips', [this.title, basePlanName]),
      );
    },
    mx_getCurrentCapacityGroupOptions(options) {
      const subAndOneMonthOptions = [], oneTimeOptions = [];
      options.forEach(capacity => {
        if (this.computedSupportCapacity.includes(capacity)) {
          if (webinarSingleUseAllowed && WEBINAR_ONE_TIME_CAPACITY.includes(capacity)) {
            oneTimeOptions.push(capacity);
          } else {
            subAndOneMonthOptions.push(capacity);
          }
        }
      });
      const hasOneMonthPlan = this.planList.find(p => p.billing_cycle === BILLING_CYCLE.ONE_MONTH);
      const subAndOneMonthOptionsLabel = hasOneMonthPlan ? this.$t('billing.billing_cycle.subscription_or_one_month') : this.$t('billing.billing_cycle.subscription');
      return [{
        label: subAndOneMonthOptionsLabel,
        options: subAndOneMonthOptions.map(c => ({capacity: c})),
        type: SUBSCRIPTION_TYPE.recurring_and_one_month
      },{
        label: this.$t('billing.billing_cycle.one_time'),
        options: oneTimeOptions.map(c => ({capacity: c})),
        type: SUBSCRIPTION_TYPE.one_time
      }];
    },
    mx_getBillingCycleGroupOptions(options) {
      const subAndOneMonthOptions = [],
        oneTimeOptions = [];
      options.forEach(item => {
        const { plan } = item;
        if (this.computedSupportCapacity.includes(plan.capacity)) {
          if (webinarSingleUseAllowed && WEBINAR_ONE_TIME_CAPACITY.includes(plan.capacity)) {
            oneTimeOptions.push(item);
          } else {
            subAndOneMonthOptions.push(item);
          }
        }
      });
      const [capacityList, oneTimeCapacityList] = [...new Set(this.planList.map(p => ({
        capacity: p.capacity,
        capacityDesc: p.capacityDesc,
      })))]
        .sort((a,b) => a.capacity - b.capacity)
        .reduce((prev, cur) => {
          const [capacityList, oneTimeCapacityList] = prev;
          if (webinarSingleUseAllowed && WEBINAR_ONE_TIME_CAPACITY.includes(cur.capacity)) {
            oneTimeCapacityList.push(cur.capacityDesc);
          } else {
            capacityList.push(cur.capacityDesc);
          }
          return prev;
        }, [[], []]);
      return [{
        label: capacityList.length && this.$t('billing.single_use_meeting.a_to_b_attendees', [capacityList[0], 10000]),
        options: subAndOneMonthOptions,
        type: SUBSCRIPTION_TYPE.recurring_and_one_month
      },{
        label: oneTimeCapacityList.length && this.$t('billing.single_use_meeting.a_to_b_attendees', [oneTimeCapacityList[0], oneTimeCapacityList[oneTimeCapacityList.length - 1]]),
        options: oneTimeOptions,
        type: SUBSCRIPTION_TYPE.one_time
      }];
    },
  }
}
export default {
  mixins: [plansMixin, planCommon],
  components: { PreviewPayment, ConfigBase, CapacityConfigList, BoughtLicense },
  data() {
    return { isMounted: false, planTypes: planTypes }
  },
  computed: {
    ...mapGetters(['isDisableAdd2Cart']),
    addFlowLink() {
      if (!this.isMounted) return;
      const { configListRef } = this.$refs;
      const basePlan = configListRef?.currentBasePlans[0] || {}
      const basePlanCount = getPlanTotalLicense(configListRef?.currentBasePlans || [])
      return `/opc/buy/add?plan=${basePlan?.category}&subPlan=newsubs&from_sub=webinar&type=add&majorPlanHost=${basePlanCount}`
    },
    addMoreVisible() {
      if (!this.isMounted) return;
      const { configListRef } = this.$refs;
      const { boughtCapacityList = [], allCapacityOrderList = [] } = configListRef;
      if (!this.isDisableAdd2Cart) {
        return allCapacityOrderList.some(x => !boughtCapacityList.includes(x)) || webinarSingleUseAllowed;
      }
      return false;
    },
    configRowName() {
      if (enableNewUIWebinarOneTimePlan) {
        return 'CapacityConfigRowV2';
      }
      return 'CapacityConfigRow';
    }
  },
  methods: {
    handleAddMore(event) {
      jumpToTargetPageForDiffType(event?.target?.href)
    }
  },
  mounted() {
    // Make $refs reactive in computed
    this.isMounted = true;
  }
};
</script>

<style lang="scss">
.opc-webinar-edit {
  &__preview-payment {
    margin-top: 24px;
  }
}
</style>
